import { useState, useCallback } from 'react'
import { useProceduresQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { generateProcedureCall, navigateToQueryEditor } from '@/utils/functionExecution'
// Define local interface for Procedure
interface Procedure {
  name: string
  schema?: string
  returnType?: string
  language?: string
  definition?: string
  owner?: string
  comment?: string
  arguments?: string  // Add arguments property for procedure parameters
}
import {
  CogIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'

interface ProceduresViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
  onProcedureSelect?: (procedureName: string) => void
}

export function ProceduresView({ connection, database, schema, onProcedureSelect }: ProceduresViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  // const [sortBy, setSortBy] = useState<'name'>('name') // Removed as only one sort field
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const { data: proceduresResponse, isLoading, error } = useProceduresQuery(
    connection.id,
    database,
    schema,
    { enabled: true, databaseType: connection.type === 'mysql' ? 'MySQL' : 'PostgreSQL' }
  )

  const allProcedures = Array.isArray(proceduresResponse) ? proceduresResponse : []

  // Filter and sort procedures
  const filteredAndSortedProcedures = allProcedures
    .filter((proc: Procedure) => {
      if (!searchTerm) return true
      return proc.name && proc.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: Procedure, b: Procedure) => {
      const aValue = a.name || ''
      const bValue = b.name || ''
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = useCallback(() => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }, [sortOrder])

  const getSortIcon = useCallback(() => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }, [sortOrder])

  // Pre-define all event handlers to avoid using useCallback in JSX
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handleNameSort = useCallback(() => {
    handleSort()
  }, [handleSort])

  const handleProcedureSelect = useCallback((procedureName: string) => {
    if (procedureName && onProcedureSelect) {
      onProcedureSelect(procedureName)
    }
  }, [onProcedureSelect])

  const handleExecuteProcedure = useCallback((proc: Procedure) => {
    if (!proc.name) return
    
    try {
      // Generate the procedure call query
      const dbType: 'mysql' | 'postgresql' = connection.type === 'mysql' ? 'mysql' : 'postgresql'
      const query = generateProcedureCall(
        proc.name,
        proc.arguments,
        dbType,
        schema,
        database
      )
      
      // Navigate to query editor with the pre-filled query
      navigateToQueryEditor(connection, query, database, schema, true)
    } catch (error) {
      console.error('Error generating procedure call:', error)
    }
  }, [connection, database, schema])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading procedures: {error.message || 'Unknown error'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <CogIcon className="w-6 h-6 mr-2 text-orange-600" />
              Procedures
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedProcedures.length} procedures
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search procedures by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      {/* Procedures Table */}
      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          {/* Table Header */}
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-8 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleNameSort}
              >
                Procedure Name
                {getSortIcon()}
              </div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedProcedures.length === 0 ? (
              <div className="text-center py-12">
                <CogIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No procedures match your criteria' : 'No procedures found'}
                </p>
              </div>
            ) : (
              filteredAndSortedProcedures.map((proc: Procedure) => (
                <div key={proc.name} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="col-span-8 flex items-center">
                    <CogIcon className="w-4 h-4 text-orange-600" />
                    <div className="ml-2">
                      <button type="button"
                        onClick={() => { handleProcedureSelect(proc.name) }}
                        className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer transition-colors"
                        title="View procedure details"
                        disabled={!proc.name}
                      >
                        {proc.name}
                      </button>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      onClick={() => { handleProcedureSelect(proc.name) }}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="View definition"
                      disabled={!proc.name}
                    >
                      <CodeBracketIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                    <button type="button"
                      onClick={() => { handleExecuteProcedure(proc) }}
                      className="p-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-colors"
                      title="Execute procedure"
                      disabled={!proc.name}
                    >
                      <PlayIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedProcedures.length} of {allProcedures.length} procedures
          </div>
        </div>
      </div>
    </div>
  )
}
